import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  Plus,
  Trash2,
  Save,
  AlertCircle,
  Eye,
  EyeOff
} from 'lucide-react';

import {
  FieldConfig,
  FieldType,
  SelectOption,
  getFieldTypeOptions,
  validateFieldConfig
} from '../../types/uiConfig';
import { StandardButton, StandardInput, StandardSelect, StandardTextarea, StandardCheckbox } from '../standard';

interface FieldConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (field: FieldConfig) => void;
  field?: FieldConfig | null;
  existingFields: FieldConfig[];
  sections: Array<{
    id: string;
    label: string;
    applicationId?: string;
    isShared?: boolean;
    sharedWith?: string[];
  }>;
}

const FieldConfigModal: React.FC<FieldConfigModalProps> = ({
  isOpen,
  onClose,
  onSave,
  field,
  existingFields,
  sections
}) => {
  const [formData, setFormData] = useState<Partial<FieldConfig>>({
    name: '',
    label: '',
    type: 'text',
    description: '',
    helpText: '',
    placeholder: '',
    isRequired: false,
    isReadOnly: false,
    isVisible: true,
    isCustom: true,
    displayOrder: existingFields.length + 1,
    columnWidth: 12,
    applicationId: 'global',
    section: sections[0]?.id || '',
    options: [],
    validation: {}
  });

  const [errors, setErrors] = useState<string[]>([]);
  const [previewField, setPreviewField] = useState(false);

  useEffect(() => {
    if (field) {
      setFormData(field);
    } else {
      setFormData({
        name: '',
        label: '',
        type: 'text',
        description: '',
        helpText: '',
        placeholder: '',
        isRequired: false,
        isReadOnly: false,
        isVisible: true,
        isCustom: true,
        displayOrder: existingFields.length + 1,
        columnWidth: 12,
        applicationId: 'global',
        section: sections[0]?.id || '',
        options: [],
        validation: {}
      });
    }
  }, [field, existingFields.length, sections]);

  const fieldTypeOptions = getFieldTypeOptions();

  const applications = [
    { value: 'global', label: 'Global/Shared' },
    { value: 'cybersweep', label: 'CyberSweep' },
    { value: 'credit-repair', label: 'Credit Repair' },
    { value: 'id-theft', label: 'ID Theft Recovery' },
    { value: 'credit-development', label: 'Credit Development' },
    { value: 'credit-block', label: 'Credit Block' }
  ];

  // Filter sections based on selected application
  const filteredSections = useMemo(() => {
    if (!sections || sections.length === 0) return [];

    const selectedApp = formData.applicationId;

    return sections.filter(section => {
      // Always show global/shared sections
      if (section.applicationId === 'global' || section.isShared === true) {
        return true;
      }

      // Show sections specific to selected application
      if (section.applicationId === selectedApp) {
        return true;
      }

      // For applications with sharedWith array
      if (section.sharedWith && Array.isArray(section.sharedWith) && selectedApp) {
        return section.sharedWith.includes(selectedApp);
      }

      return false;
    });
  }, [sections, formData.applicationId]);

  const handleInputChange = (name: string, value: any) => {
    setFormData(prev => {
      const updates: any = { ...prev, [name]: value };

      // If application changes, reset section to first available in new app
      if (name === 'applicationId') {
        const newFilteredSections = sections.filter(s =>
          s.applicationId === value || (s.isShared && s.sharedWith?.includes(value))
        );
        updates.section = newFilteredSections[0]?.id || '';
      }

      return updates;
    });
  };

  const handleAddOption = () => {
    const newOption: SelectOption = {
      value: '',
      label: '',
      isActive: true,
      isDefault: false
    };
    setFormData(prev => ({
      ...prev,
      options: [...(prev.options || []), newOption]
    }));
  };

  const handleRemoveOption = (index: number) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options?.filter((_, i) => i !== index) || []
    }));
  };

  const handleOptionChange = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options?.map((option, i) =>
        i === index ? { ...option, [field]: value } : option
      ) || []
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Generate ID and set metadata
    const fieldConfig: FieldConfig = {
      ...formData,
      id: field?.id || `custom_${formData.name}_${Date.now()}`,
      createdBy: field?.createdBy || 'current_user',
      createdDate: field?.createdDate || new Date().toISOString(),
      modifiedBy: 'current_user',
      modifiedDate: new Date().toISOString()
    } as FieldConfig;

    // Validate
    const validationErrors = validateFieldConfig(fieldConfig);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    onSave(fieldConfig);
    onClose();
  };

  const renderFieldPreview = () => {
    if (!previewField || !formData.type) return null;

    const commonProps = {
      placeholder: formData.placeholder,
      disabled: formData.isReadOnly,
      required: formData.isRequired,
      className: "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    };

    switch (formData.type) {
      case 'text':
      case 'email':
      case 'url':
        return <input type={formData.type} {...commonProps} />;
      case 'phone':
        return <input type="tel" {...commonProps} />;
      case 'number':
      case 'currency':
        return <input type="number" {...commonProps} />;
      case 'date':
        return <input type="date" {...commonProps} />;
      case 'datetime':
        return <input type="datetime-local" {...commonProps} />;
      case 'textarea':
        return <textarea rows={3} {...commonProps} />;
      case 'select':
        return (
          <select {...commonProps}>
            <option value="">Select an option...</option>
            {formData.options?.map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      case 'checkbox':
        return (
          <label className="flex items-center">
            <input type="checkbox" className="mr-2" disabled={formData.isReadOnly} />
            <span>{formData.label}</span>
          </label>
        );
      case 'radio':
        return (
          <div className="space-y-2">
            {formData.options?.map((option, index) => (
              <label key={index} className="flex items-center">
                <input
                  type="radio"
                  name={`preview_${formData.name}`}
                  value={option.value}
                  className="mr-2"
                  disabled={formData.isReadOnly}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        );
      default:
        return <input type="text" {...commonProps} />;
    }
  };

  if (!isOpen) return null;

  const requiresOptions = ['select', 'multiselect', 'radio'].includes(formData.type || '');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto m-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {field ? 'Edit Field' : 'Create Custom Field'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {errors.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                <div>
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
                    Please fix the following errors:
                  </h3>
                  <ul className="mt-2 text-sm text-red-700 dark:text-red-400 list-disc list-inside">
                    {errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Basic Information</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
                  Field Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="firstName, creditScore, etc."
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  API name (alphanumeric and underscores only)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Display Label <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.label || ''}
                  onChange={(e) => handleInputChange('label', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="First Name, Credit Score, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Application <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.applicationId || 'global'}
                  onChange={(e) => handleInputChange('applicationId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  {applications.map(app => (
                    <option key={app.value} value={app.value}>
                      {app.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Which application or module will use this field
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Field Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.type || 'text'}
                  onChange={(e) => handleInputChange('type', e.target.value as FieldType)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  {fieldTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {fieldTypeOptions.find(opt => opt.value === formData.type)?.description}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Section/Object Type
                </label>

                <select
                  value={formData.section || ''}
                  onChange={(e) => handleInputChange('section', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a section...</option>

                  {/* Group global sections */}
                  {filteredSections.filter(s => s.applicationId === 'global').length > 0 && (
                    <optgroup label="Global Sections">
                      {filteredSections
                        .filter(s => s.applicationId === 'global')
                        .map(section => (
                          <option key={section.id} value={section.id}>
                            {section.label}
                          </option>
                        ))}
                    </optgroup>
                  )}

                  {/* Group application-specific sections */}
                  {filteredSections.filter(s => s.applicationId === formData.applicationId).length > 0 && (
                    <optgroup label={`${applications.find(a => a.value === formData.applicationId)?.label} Sections`}>
                      {filteredSections
                        .filter(s => s.applicationId === formData.applicationId)
                        .map(section => (
                          <option key={section.id} value={section.id}>
                            {section.label}
                          </option>
                        ))}
                    </optgroup>
                  )}

                  {filteredSections.length === 0 && (
                    <option value="">No sections available for this application</option>
                  )}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Sections available for {applications.find(a => a.value === formData.applicationId)?.label}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Brief description of this field's purpose"
                />
              </div>
            </div>

            {/* Configuration */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Configuration</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Column Width
                  </label>
                  <select
                    value={formData.columnWidth || 12}
                    onChange={(e) => handleInputChange('columnWidth', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={12}>Full Width (12)</option>
                    <option value={6}>Half Width (6)</option>
                    <option value={4}>Third Width (4)</option>
                    <option value={3}>Quarter Width (3)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.displayOrder || 1}
                    onChange={(e) => handleInputChange('displayOrder', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500"
                    min="1"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Placeholder Text
                </label>
                <input
                  type="text"
                  value={formData.placeholder || ''}
                  onChange={(e) => handleInputChange('placeholder', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter placeholder text..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Help Text
                </label>
                <input
                  type="text"
                  value={formData.helpText || ''}
                  onChange={(e) => handleInputChange('helpText', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Additional help text for users"
                />
              </div>

              {/* Field Options */}
              <div className="space-y-3">
                <StandardCheckbox
                  label="Required Field"
                  checked={formData.isRequired || false}
                  onChange={(checked) => handleInputChange('isRequired', checked)}
                />

                <StandardCheckbox
                  label="Read Only"
                  checked={formData.isReadOnly || false}
                  onChange={(checked) => handleInputChange('isReadOnly', checked)}
                />

                <StandardCheckbox
                  label="Visible"
                  checked={formData.isVisible !== false}
                  onChange={(checked) => handleInputChange('isVisible', checked)}
                />
              </div>

              {/* Preview Toggle */}
              <div className="border-t border-gray-200 pt-4">
                <StandardButton
                  type="button"
                  variant="ghost"
                  onClick={() => setPreviewField(!previewField)}
                  leftIcon={previewField ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                >
                  {previewField ? 'Hide Preview' : 'Show Preview'}
                </StandardButton>
              </div>
            </div>
          </div>

          {/* Options for Select/Radio/Multiselect */}
          {requiresOptions && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Options</h3>
                <button
                  type="button"
                  onClick={handleAddOption}
                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 flex items-center"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Option
                </button>
              </div>

              <div className="space-y-3">
                {formData.options?.map((option, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex-1 grid grid-cols-3 gap-3">
                      <input
                        type="text"
                        placeholder="Value"
                        value={option.value}
                        onChange={(e) => handleOptionChange(index, 'value', e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="Label"
                        value={option.label}
                        onChange={(e) => handleOptionChange(index, 'label', e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="flex items-center space-x-2">
                        <label className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={option.isDefault || false}
                            onChange={(e) => handleOptionChange(index, 'isDefault', e.target.checked)}
                            className="mr-1"
                          />
                          Default
                        </label>
                        <label className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={option.isActive !== false}
                            onChange={(e) => handleOptionChange(index, 'isActive', e.target.checked)}
                            className="mr-1"
                          />
                          Active
                        </label>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveOption(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Field Preview */}
          {previewField && (
            <div className="space-y-4 border-t border-gray-200 pt-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Field Preview</h3>
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {formData.label}
                  {formData.isRequired && <span className="text-red-500 ml-1">*</span>}
                </label>
                {renderFieldPreview()}
                {formData.helpText && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{formData.helpText}</p>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 border-t border-gray-200 dark:border-gray-700 pt-6">
            <StandardButton
              type="button"
              variant="secondary"
              onClick={onClose}
            >
              Cancel
            </StandardButton>
            <StandardButton
              type="submit"
              variant="primary"
              leftIcon={<Save className="h-4 w-4" />}
            >
              {field ? 'Update Field' : 'Create Field'}
            </StandardButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FieldConfigModal;